/**
 * Pure helpers for rendering `deliverable_revisions.version_date`
 * (migration 372) in the operator + public surfaces.
 *
 * The DB column is a Postgres `date` (no timezone). PostgREST
 * returns it as `"YYYY-MM-DD"`. We parse manually instead of
 * `new Date(string)` because the latter interprets bare
 * date-only strings as UTC midnight, which then renders
 * one day earlier in any negative-offset timezone (a "May 7,
 * 2026" stamp in the DB rendering as "May 6" for an EDT
 * operator). Manual parse keeps the day stable.
 */

/**
 * Format a YYYY-MM-DD string (or null/undefined/blank) as an
 * en-US short date like "May 7, 2026". Pin the locale so SSR
 * and the browser never disagree (React hydration #418). Returns
 * null on missing / unparseable input so callers can omit the
 * segment entirely instead of rendering "Invalid Date".
 */
export function formatVersionDate(
  value: string | null | undefined,
  locale = "en-US",
): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    month < 0 ||
    month > 11 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }
  // Construct in local time so toLocaleDateString doesn't
  // shift across the date boundary for negative-offset zones.
  const date = new Date(year, month, day);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Returns true when `value` is a syntactically-valid YYYY-MM-DD
 * string. Used by the version-date editor before sending to
 * the server action so a client-side validation message
 * surfaces faster than a roundtrip rejection.
 */
export function isIsoDateString(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * Pull the first non-empty line out of a multi-line note for
 * the inline preview that renders next to the collapsed
 * version row in `VersionSection`. Trims surrounding
 * whitespace; returns null when the note is empty / null /
 * whitespace-only so the caller can omit the segment entirely
 * instead of rendering an empty pill.
 *
 * Why first non-empty line (not first 60 chars): operators
 * routinely paste customer feedback with a leading blank line
 * or markdown-style heading, and a hard char cut would slice
 * the second paragraph mid-sentence. Picking the first
 * substantive line + relying on CSS truncation in the
 * renderer produces a clean preview without losing the
 * punchline.
 */
export function firstNonEmptyLine(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const lines = trimmed.split(/\r?\n/);
  for (const line of lines) {
    const t = line.trim();
    if (t) return t;
  }
  return null;
}
